import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { toast } from 'react-toastify';

const CouponContext = createContext();

const couponReducer = (state, action) => {
  switch (action.type) {
    case 'APPLY_COUPON':
      return {
        ...state,
        appliedCoupon: action.payload,
        error: null,
        isLoading: false
      };
    case 'REMOVE_COUPON':
      return {
        ...state,
        appliedCoupon: null,
        error: null,
        isLoading: false
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    case 'SET_COUPON_STATUS':
      return {
        ...state,
        couponStatus: action.payload
      };
    default:
      return state;
  }
};

export const CouponProvider = ({ children }) => {
  const [state, dispatch] = useReducer(couponReducer, {
    appliedCoupon: null,
    error: null,
    isLoading: false,
    couponStatus: 'idle' // idle, loading, success, error
  });

  // Load coupon from localStorage on mount
  useEffect(() => {
    const savedCoupon = localStorage.getItem('appliedCoupon');
    if (savedCoupon) {
      try {
        const coupon = JSON.parse(savedCoupon);
        dispatch({ type: 'APPLY_COUPON', payload: coupon });
      } catch (error) {
        console.error('Error loading coupon from localStorage:', error);
        localStorage.removeItem('appliedCoupon');
      }
    }
  }, []);

  // Save coupon to localStorage when it changes
  useEffect(() => {
    if (state.appliedCoupon) {
      localStorage.setItem('appliedCoupon', JSON.stringify(state.appliedCoupon));
    } else {
      localStorage.removeItem('appliedCoupon');
    }
  }, [state.appliedCoupon]);

  useEffect(() => {
    if (!state.appliedCoupon || !state.appliedCoupon.code) return;
    const q = query(collection(db, 'coupons'), where('code', '==', state.appliedCoupon.code));
    let unsubscribe;
    getDocs(q).then(snapshot => {
      if (!snapshot.empty) {
        const couponDoc = snapshot.docs[0];
        const couponRef = doc(db, 'coupons', couponDoc.id);
        unsubscribe = onSnapshot(couponRef, (docSnap) => {
          const data = docSnap.data();
          if (!data) return;
          if (data.used || (data.usedCount && data.usedCount >= 1) || data.locked || (data.expiryDate && data.expiryDate < Date.now())) {
            removeCoupon();
            toast.info('Coupon expired or used. Please enter a new coupon.');
          }
        });
      }
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  // eslint-disable-next-line
  }, [state.appliedCoupon && state.appliedCoupon.code]);

  const applyCoupon = async (couponCode) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const normalizedCode = couponCode.trim().toUpperCase();
      
      // Validate coupon format
      if (!normalizedCode.startsWith("HVT-")) {
        const errorMsg = "Invalid coupon code format. Must start with HVT-";
        dispatch({ type: 'SET_ERROR', payload: errorMsg });
        dispatch({ type: 'SET_COUPON_STATUS', payload: 'error' });
        throw new Error(errorMsg);
      }

      // Find coupon in Firestore
      const q = query(collection(db, "coupons"), where("code", "==", normalizedCode));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        const errorMsg = "Coupon not found.";
        dispatch({ type: 'SET_ERROR', payload: errorMsg });
        dispatch({ type: 'SET_COUPON_STATUS', payload: 'error' });
        throw new Error(errorMsg);
      }

      const couponDoc = snapshot.docs[0];
      const coupon = couponDoc.data();

      // Check expiry
      if (coupon.expiryDate && coupon.expiryDate < Date.now()) {
        const errorMsg = "Coupon expired.";
        dispatch({ type: 'SET_ERROR', payload: errorMsg });
        dispatch({ type: 'SET_COUPON_STATUS', payload: 'error' });
        throw new Error(errorMsg);
      }

      // Check if already used
      if (coupon.used || (coupon.usedCount && coupon.usedCount >= 1)) {
        const errorMsg = "Coupon already used.";
        dispatch({ type: 'SET_ERROR', payload: errorMsg });
        dispatch({ type: 'SET_COUPON_STATUS', payload: 'error' });
        throw new Error(errorMsg);
      }

      // Check if locked
      if (coupon.locked && coupon.reservedOrderId) {
        const errorMsg = "Coupon already in use.";
        dispatch({ type: 'SET_ERROR', payload: errorMsg });
        dispatch({ type: 'SET_COUPON_STATUS', payload: 'error' });
        throw new Error(errorMsg);
      }

      // Check usage limit
      if (coupon.maxUses && coupon.usedCount >= coupon.maxUses) {
        const errorMsg = "Coupon usage limit reached.";
        dispatch({ type: 'SET_ERROR', payload: errorMsg });
        dispatch({ type: 'SET_COUPON_STATUS', payload: 'error' });
        throw new Error(errorMsg);
      }

      // Get vendor discount if available
      let vendorDefaultDiscount = 0;
      const lastVendorId = sessionStorage.getItem("lastScannedVendorId");
      
      if (lastVendorId) {
        const vendorDoc = await getDoc(doc(db, "vendors", lastVendorId));
        if (vendorDoc.exists()) {
          vendorDefaultDiscount = vendorDoc.data().defaultDiscountPercentage || 0;
        }
      }

      const couponWithVendorDiscount = {
        ...coupon,
        vendorDefaultDiscount,
        appliedAt: new Date().toISOString(),
        status: 'applied'
      };

      dispatch({ type: 'APPLY_COUPON', payload: couponWithVendorDiscount });
      dispatch({ type: 'SET_COUPON_STATUS', payload: 'success' });
    } catch (error) {
      // Ensure error is properly set in state
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_COUPON_STATUS', payload: 'error' });
      // Also show toast immediately for better UX
      toast.error(error.message || "Failed to apply coupon");
      throw error; // Re-throw so the calling component can handle it
    }
  };

  const removeCoupon = () => {
    dispatch({ type: 'REMOVE_COUPON' });
    dispatch({ type: 'SET_COUPON_STATUS', payload: 'idle' });
    localStorage.removeItem('appliedCoupon');
  };

  const checkAssignedCoupon = async (userEmail) => {
    if (!userEmail || state.appliedCoupon) return;
    
    try {
      const q = query(
        collection(db, "coupons"), 
        where("assignedTo", "==", userEmail)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const couponDoc = snapshot.docs[0];
        const coupon = couponDoc.data();
        
        // Check if coupon is still valid and not used
        if (!coupon.used && 
            (!coupon.usedCount || coupon.usedCount < 1) && 
            coupon.expiryDate && coupon.expiryDate > Date.now()) {
          await applyCoupon(coupon.code);
          toast.success(`Welcome! Your assigned coupon ${coupon.code} has been applied automatically.`);
        }
      }
    } catch (error) {
      console.error('Error checking assigned coupon:', error);
    }
  };

  const value = {
    ...state,
    applyCoupon,
    removeCoupon,
    checkAssignedCoupon
  };

  return (
    <CouponContext.Provider value={value}>
      {children}
    </CouponContext.Provider>
  );
};

export const useCoupon = () => {
  const context = useContext(CouponContext);
  if (!context) {
    throw new Error('useCoupon must be used within a CouponProvider');
  }
  return context;
}; 